/**
 * SecureField Component
 * Field-level security that masks data based on permissions
 */

import React, { useEffect } from 'react';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { logFieldAccess } from '../audit';
import { PermissionScope } from '@/types';

interface Permission {
  resource: string;
  action: string;
  scope?: PermissionScope;
}

interface SecureFieldProps {
  value: unknown;
  permission: Permission;
  mask?: string | React.ReactNode;
  formatter?: (value: unknown) => string;
  auditAccess?: boolean;
}

export const SecureField: React.FC<SecureFieldProps> = ({
  value,
  permission,
  mask = '••••',
  formatter,
  auditAccess = true
}) => {
  const { hasPermission } = useAuth();

  const canView = hasPermission({
    resource: permission.resource,
    action: permission.action,
    scope: permission.scope || PermissionScope.SELF
  });

  // Log field access
  useEffect(() => {
    if (auditAccess) {
      logFieldAccess(permission.resource, permission, canView);
    }
  }, [permission, canView, auditAccess]);

  if (!canView) {
    return <span className='text-muted-foreground'>{mask}</span>;
  }

  const displayValue = formatter ? formatter(value) : String(value ?? '');
  return <span>{displayValue}</span>;
};
